/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/**
 * Filter product color swatches by shade group.
 *
 * Quick-buy drawers and popovers are fetched and inserted after page load.
 * Keeping this behavior in the globally loaded custom.js makes it available
 * to both the PDP and dynamically rendered quick-buy content.
 */
(() => {
  if (window.SKShadeTabs) {
    return;
  }

  window.SKShadeTabs = {
    init(root = document) {
      if (!root.querySelectorAll) {
        return;
      }

      root.querySelectorAll('[data-shade-tabs]').forEach((tabs) => {
        if (tabs.dataset.shadeTabsInitialized === 'true') {
          return;
        }

        tabs.dataset.shadeTabsInitialized = 'true';
        this.apply(tabs, 'all', false);
      });
    },

    apply(tabs, group, shouldChangeSelection) {
      const selector = tabs.closest('.product-form__option-selector');
      const list = selector ? selector.querySelector('[data-shade-swatch-list]') : null;

      if (!list) {
        return;
      }

      tabs.querySelectorAll('[data-shade-tab]').forEach((tab) => {
        const isActive = tab.dataset.shadeTab === group;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (shouldChangeSelection) {
        this.centerActiveTab(tabs, group);
      }

      list.querySelectorAll('.color-swatch').forEach((swatch) => {
        const swatchGroups = (swatch.dataset.shadeGroup || '')
          .trim()
          .split(/\s+/)
          .filter(Boolean);
        const isVisible = group === 'all' || swatchGroups.includes(group);
        swatch.classList.toggle('sk-shade-group-hidden', !isVisible);
      });

      if (!shouldChangeSelection || group === 'all') {
        return;
      }

      const checkedInput = list.querySelector('.color-swatch__radio:checked');
      const checkedSwatch = checkedInput ? checkedInput.closest('.color-swatch') : null;

      if (checkedSwatch && !checkedSwatch.classList.contains('sk-shade-group-hidden')) {
        return;
      }

      const nextInput = Array.from(list.querySelectorAll('.color-swatch__radio')).find((input) => {
        const swatch = input.closest('.color-swatch');

        return (
          swatch &&
          !input.disabled &&
          !swatch.hidden &&
          !swatch.classList.contains('is-disabled') &&
          !swatch.classList.contains('sk-shade-group-hidden')
        );
      });

      if (nextInput && !nextInput.checked) {
        nextInput.checked = true;
        nextInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },

    centerActiveTab(tabs, group) {
      const escapedGroup = window.CSS && CSS.escape ? CSS.escape(group) : group.replace(/"/g, '\\"');
      const activeTab = tabs.querySelector(`[data-shade-tab="${escapedGroup}"]`);

      if (!activeTab) {
        return;
      }

      const targetLeft = activeTab.offsetLeft - (tabs.clientWidth - activeTab.offsetWidth) / 2;

      tabs.scrollTo({
        left: Math.max(targetLeft, 0),
        behavior: 'smooth',
      });
    },
  };

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-shade-tab]');

    if (!tab) {
      return;
    }

    const tabs = tab.closest('[data-shade-tabs]');

    if (!tabs) {
      return;
    }

    window.SKShadeTabs.apply(tabs, tab.dataset.shadeTab, false);
    window.SKShadeTabs.centerActiveTab(tabs, tab.dataset.shadeTab);
  });

  document.addEventListener('shopify:section:load', (event) => {
    window.SKShadeTabs.init(event.target);
  });

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) {
          return;
        }

        if (node.matches('[data-shade-tabs]')) {
          window.SKShadeTabs.init(node.parentElement || document);
        } else if (node.querySelector('[data-shade-tabs]')) {
          window.SKShadeTabs.init(node);
        }
      });
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.SKShadeTabs.init(document);
})();

/**
 * After adding from quick buy, close its drawer or popover and let the
 * refreshed cart markup render before opening the mini-cart.
 */
(() => {
  let shouldOpenMiniCart = false;

  document.addEventListener('variant:added', (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const quickBuy = event.target.closest('quick-buy-drawer, quick-buy-popover');

    if (!quickBuy) {
      return;
    }

    quickBuy.open = false;
    shouldOpenMiniCart = true;
  });

  document.addEventListener(
    'cart:refresh',
    (event) => {
      if (!shouldOpenMiniCart || !event.detail) {
        return;
      }

      event.detail.openMiniCart = true;
      shouldOpenMiniCart = false;
    },
    true,
  );
})();

/**
 * Keep PDP complementary-product navigation in sync with the native
 * horizontal scroller. The recommendations markup is loaded asynchronously,
 * so the custom element initializes after Shopify inserts the response.
 */
class ComplementaryProductsSlider extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-complementary-products-viewport]');
    this.controls = this.querySelector('[data-complementary-products-controls]');
    this.prevButton = this.querySelector('[data-complementary-products-prev]');
    this.nextButton = this.querySelector('[data-complementary-products-next]');
    this.dots = this.querySelector('[data-complementary-products-dots]');
    this.items = Array.from(this.querySelectorAll('.product-item'));

    if (!this.viewport || !this.controls || !this.prevButton || !this.nextButton || !this.dots) {
      return;
    }

    this.direction = document.documentElement.dir === 'rtl' ? -1 : 1;
    this.handleScroll = this.handleScroll.bind(this);
    this.refresh = this.refresh.bind(this);

    this.prevButton.addEventListener('click', () => this.goTo(this.activeIndex - 1));
    this.nextButton.addEventListener('click', () => this.goTo(this.activeIndex + 1));
    this.viewport.addEventListener('scroll', this.handleScroll, { passive: true });

    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.refresh);
      this.resizeObserver.observe(this.viewport);
    }

    this.refresh();
  }

  disconnectedCallback() {
    if (this.viewport && this.handleScroll) {
      this.viewport.removeEventListener('scroll', this.handleScroll);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
    }
  }

  refresh() {
    const maxScroll = Math.max(this.viewport.scrollWidth - this.viewport.clientWidth, 0);
    const firstItemOffset = this.items[0] ? this.items[0].offsetLeft : 0;

    this.positions = this.items
      .map((item) => Math.min(Math.max(Math.abs(item.offsetLeft - firstItemOffset), 0), maxScroll))
      .filter((position, index, positions) => index === 0 || Math.abs(position - positions[index - 1]) > 1);

    this.controls.hidden = this.positions.length <= 1;
    this.renderDots();
    this.update();
  }

  renderDots() {
    this.dots.replaceChildren(
      ...this.positions.map((position, index) => {
        const dot = document.createElement('button');

        dot.type = 'button';
        dot.className = 'product-content__featured-products-dot';
        dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
        dot.addEventListener('click', () => this.goTo(index));

        return dot;
      }),
    );
  }

  handleScroll() {
    if (this.scrollFrame) {
      cancelAnimationFrame(this.scrollFrame);
    }

    this.scrollFrame = requestAnimationFrame(() => this.update());
  }

  update() {
    const currentPosition = Math.abs(this.viewport.scrollLeft);

    this.activeIndex = this.positions.reduce((closestIndex, position, index) => {
      return Math.abs(position - currentPosition) < Math.abs(this.positions[closestIndex] - currentPosition)
        ? index
        : closestIndex;
    }, 0);

    this.prevButton.hidden = currentPosition <= 1;
    this.nextButton.hidden = currentPosition >= this.positions[this.positions.length - 1] - 1;

    Array.from(this.dots.children).forEach((dot, index) => {
      const isActive = index === this.activeIndex;

      dot.classList.toggle('is-active', isActive);
      dot.toggleAttribute('aria-current', isActive);
    });
  }

  goTo(index) {
    const nextIndex = Math.min(Math.max(index, 0), this.positions.length - 1);

    this.viewport.scrollTo({
      left: this.positions[nextIndex] * this.direction,
      behavior: 'smooth',
    });
  }
}

if (!window.customElements.get('complementary-products-slider')) {
  window.customElements.define('complementary-products-slider', ComplementaryProductsSlider);
}

/**
 * US 站点重构地区选择器跳转逻辑
 */
class CountryLocalizationForm extends HTMLElement {
  _getCountryDomains() {
    return window.themeVariables?.settings?.countryRedirectDomains || {};
  }

  connectedCallback() {
    this._onClick = this._onClick.bind(this);
    this.addEventListener('click', this._onClick, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick, true);
  }

  _onClick(event) {
    const button = event.target.closest('button[name="country_code"]');

    if (!button) {
      return;
    }

    const countryCode = String(button.value || '')
      .trim()
      .toUpperCase();

    if (!countryCode || countryCode === 'US') {
      return;
    }

    const targetDomain = this._getCountryDomains()[countryCode];

    if (!targetDomain) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    window.location.href = this._buildUrl(targetDomain);
  }

  _buildUrl(domain) {
    const target = domain.replace(/^https?:\/\//, '').replace(/\/+$/, '');
    const slashIndex = target.indexOf('/');

    const host = slashIndex === -1 ? target : target.slice(0, slashIndex);
    const marketPrefix =
      slashIndex === -1 ? '' : '/' + target.slice(slashIndex + 1).replace(/^\/+|\/+$/g, '');

    let cleanPath = window.location.pathname.replace(/^\/([a-z]{2}(?:-[a-z]{2})?)(?=\/|$)/i, '') || '/';

    if (!cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }

    const shouldKeepPath =
      cleanPath !== '/' &&
      !cleanPath.startsWith('/cart') &&
      !cleanPath.startsWith('/checkout') &&
      !cleanPath.startsWith('/account');

    let targetUrl = 'https://' + host + marketPrefix;

    if (shouldKeepPath) {
      targetUrl += cleanPath;
    }

    targetUrl += window.location.search;

    return targetUrl;
  }
}

if (!window.customElements.get('country-localization-form')) {
  window.customElements.define('country-localization-form', CountryLocalizationForm);
}

/**
 * Deferred image loading (variant / interactive images).
 * Used by build-your-bundle and deferred-image snippet.
 */
(() => {
  if (window.SKDeferredImages) {
    return;
  }

  function loadDeferredImages(root) {
    if (!root || !root.querySelectorAll) {
      return;
    }

    root.querySelectorAll('img[data-deferred-src]:not([data-deferred-loaded])').forEach((img) => {
      void window.SKDeferredImages.loadSingle(img);
    });
  }

  window.SKDeferredImages = {
    load: loadDeferredImages,
    loadSingle(img) {
      if (!img || !img.hasAttribute('data-deferred-src') || img.hasAttribute('data-deferred-loaded')) {
        return Promise.resolve();
      }

      const src = img.getAttribute('data-deferred-src');

      if (!src) {
        return Promise.resolve();
      }

      img.src = src.startsWith('//') ? `${window.location.protocol}${src}` : src;

      const srcset = img.getAttribute('data-deferred-srcset');

      if (srcset) {
        img.srcset = srcset;
      }

      const sizes = img.getAttribute('data-deferred-sizes');

      if (sizes) {
        img.sizes = sizes;
      }

      img.setAttribute('data-deferred-loaded', 'true');
      img.removeAttribute('data-deferred-src');
      img.removeAttribute('data-deferred-srcset');
      img.removeAttribute('data-deferred-sizes');

      if (img.complete && img.naturalWidth > 0) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    },
  };
})();

/**
 * Lazy-load product card secondary images on hover (data-hover-src).
 * Uses document delegation so dynamically loaded Bundle cards also work.
 */
(() => {
  if (window.SKProductItemSecondaryHover) {
    return;
  }

  function loadSecondaryHoverImage(img) {
    if (!img || !img.hasAttribute('data-hover-src') || img.hasAttribute('data-hover-loaded')) {
      return Promise.resolve();
    }

    const src = img.getAttribute('data-hover-src');

    if (!src) {
      return Promise.resolve();
    }

    img.src = src.startsWith('//') ? `${window.location.protocol}${src}` : src;

    const srcset = img.getAttribute('data-hover-srcset');

    if (srcset) {
      img.srcset = srcset;
    }

    const sizes = img.getAttribute('data-hover-sizes');

    if (sizes) {
      img.sizes = sizes;
    }

    img.setAttribute('data-hover-loaded', 'true');
    img.removeAttribute('data-hover-src');
    img.removeAttribute('data-hover-srcset');
    img.removeAttribute('data-hover-sizes');

    if (img.complete && img.naturalWidth > 0) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    });
  }

  document.addEventListener(
    'pointerenter',
    (event) => {
      const wrapper = event.target.closest?.('.product-item__image-wrapper--multiple');

      if (!wrapper) {
        return;
      }

      const img = wrapper.querySelector('img.product-item__secondary-image[data-hover-src]');

      if (img) {
        void loadSecondaryHoverImage(img);
      }
    },
    true,
  );

  window.SKProductItemSecondaryHover = {
    load: loadSecondaryHoverImage,
  };
})();